import { getCountryByCurrency } from '@utils/locations';
import { useCallback, useEffect, useState } from 'react';

import { SELECTED_PRODUCTS_BY_ID_QUERY } from '../apollo/shopify/queries';
import { storefrontClient } from '../apollo/shopify/storefrontClient';
import { ShopifyGenSelectedProductsQuery } from '../types/shopifygen';
import { mapShopifyProductsToProducts } from '../utils/product';
import { listGraphqlIds } from '../utils/shopifyIds';
import { useCurrency } from './useCurrency';

type StoryblokData = {
  data: {
    stories: ProductPageContent[];
  };
  perPage: number;
  total: number;
};

export type ProductByUuid = {
  shopify: Product | undefined;
  storyblok: Partial<ProductType> | undefined;
};

export type ProductByUuidState = {
  loading: boolean;
  products?: Record<string, ProductByUuid>;
};

const storiesGraphqlIds = (stories: ProductPageContent[] = []) => {
  return stories.flatMap(({ content }) => listGraphqlIds(content?.id));
};

interface Options {
  excludingFields?: string;
  storyblokUuids: string[];
}

export function useProductsByUuid({
  excludingFields,
  storyblokUuids,
}: Options): ProductByUuidState {
  const uuids = storyblokUuids.join(',');
  const [currency] = useCurrency();

  const [state, setState] = useState<ProductByUuidState>({
    loading: Boolean(storyblokUuids),
    products: undefined,
  });

  const fetchStoryblok = useCallback(
    async (byUuids: string) => {
      try {
        const res = await fetch('/api/storyblok/stories', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            byUuids,
            excludingFields,
            page: 1,
            perPage: 50,
          }),
        });
        const { data } = (await res.json()) as StoryblokData;
        return data.stories;
      } catch (err) {
        console.error(err);
        return [];
      }
    },
    [excludingFields],
  );

  const fetchShopify = useCallback(
    async (ids: string[]) => {
      try {
        const { data } =
          await storefrontClient.query<ShopifyGenSelectedProductsQuery>({
            query: SELECTED_PRODUCTS_BY_ID_QUERY,
            variables: {
              country: getCountryByCurrency(currency),
              ids,
            },
            fetchPolicy: 'no-cache',
          });

        const results = data.nodes.filter(
          (node) => node?.__typename === 'Product',
        ) as Extract<
          ShopifyGenSelectedProductsQuery['nodes'][number],
          { __typename?: 'Product' }
        >[];
        // @ts-ignore
        return mapShopifyProductsToProducts(results.filter(Boolean));
      } catch (err) {
        console.error(err);
        return [];
      }
    },
    [currency],
  );

  const fetchProducts = useCallback(
    async (_uuids: string) => {
      const storybloks = await fetchStoryblok(_uuids);
      const shopifys = await fetchShopify(storiesGraphqlIds(storybloks));
      return { storybloks, shopifys };
    },
    [fetchShopify, fetchStoryblok],
  );

  useEffect(() => {
    if (!uuids) {
      return;
    }
    fetchProducts(uuids).then(({ shopifys, storybloks }) => {
      const products = storybloks.reduce(
        (acc, curr) => {
          acc[curr.uuid] = {
            storyblok: curr.content,
            shopify: shopifys.find(({ id }) =>
              listGraphqlIds(curr.content.id).includes(id),
            ),
          };
          return acc;
        },
        {} as Record<string, ProductByUuid>,
      );
      setState({ loading: false, products });
    });
  }, [fetchProducts, uuids]);

  return state;
}
